import CountUp from '@/components/base/CountUp';
import { formatMoney } from '@/utils/format';
import type { Wallet } from '@/types/portal';
import { PLATFORM } from '@/config/platform';

/**
 * The agency's funding balance as a wallet card: burgundy, with a slow glossy light, the KES
 * total counting up and one chip per currency float.
 */
export default function FundingBalanceCard({ totalKes, wallets }: { totalKes: number; wallets: Wallet[] }) {
  return (
    <article className="wallet-card brand-gradient flex min-h-[148px] flex-col justify-between rounded-xl p-5 text-background-50 shadow-[0_18px_40px_-22px] shadow-primary-900/70">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-label font-medium uppercase tracking-wider text-background-50/75">Funding balance</p>
          <p className="mt-2 font-heading text-3xl font-semibold tabular-nums">
            <CountUp value={formatMoney(totalKes, 'KES')} duration={1200} />
          </p>
          <p className="mt-0.5 text-xs text-background-50/70">
            Across {wallets.length} currency {wallets.length === 1 ? 'wallet' : 'wallets'} · KES equivalent
          </p>
        </div>
        {/* A card chip and the Twiva Pay mark, for the wallet-card look. */}
        <div className="flex flex-col items-end gap-2" aria-hidden="true">
          <span className="h-7 w-10 rounded-md bg-gradient-to-br from-amber-200 via-amber-300 to-amber-500 shadow-inner" />
          <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-background-50/60">{PLATFORM.payName}</span>
        </div>
      </div>
      {wallets.length > 0 ? (
        <div className="mt-4 flex flex-wrap gap-1.5">
          {wallets.map((wallet) => (
            <span
              key={wallet.id}
              className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-2.5 py-1 text-xs font-medium backdrop-blur-sm"
            >
              <span className={`h-1.5 w-1.5 rounded-full ${wallet.status === 'Wallet active' || wallet.status === 'Active' ? 'bg-accent-300' : 'bg-secondary-300'}`} />
              {formatMoney(wallet.balance, wallet.currency)}
            </span>
          ))}
        </div>
      ) : null}
    </article>
  );
}
